import type { ChecklistStepMeta } from "../types";

/**
 * 6 项起步清单步骤元数据 — Figma「Edgen - 開發文檔」V3.1.1 checklist 组字面量
 * 按 stepId 1->6 严格排序
 *
 * cardDescription：invest 为 Figma 字面量，其余步骤 Figma 未给卡片短文案，
 * 按 description 首句同模式派生（ff / notif / banking / tracker / goal）。
 * runningName：完成页 NowRunning 行名，Figma 与 title 不同（短名）。
 */
export const CHECKLIST_STEP_META: ChecklistStepMeta[] = [
  {
    stepId: 1,
    stepKey: "ff_score",
    title: "Get financial fitness score",
    description: "One number for where you stand, and what raises it.",
    actionLabel: "Get Score",
    cardDescription: "One number for where you stand.",
    runningLabel: "%{var0}/100",
    runningName: "Financial fitness score",
  },
  {
    stepId: 2,
    stepKey: "notification",
    title: "Turn on notification",
    description:
      "Ed will flag the moves worth a look on the things you're tracking.",
    actionLabel: "Turn on",
    cardDescription: "Ed flags the moves worth a look.",
    runningLabel: "On",
    runningName: "Notifications",
  },
  {
    stepId: 3,
    stepKey: "invest_account",
    title: "Add invest accounts",
    description:
      "Get a full read on your portfolio: what you hold, how it's doing, what's worth a second look.",
    actionLabel: "Add an account",
    cardDescription: "Get a full read on your portfolio.",
    runningLabel: "Connected",
    runningName: "Investment accounts",
  },
  {
    stepId: 4,
    stepKey: "banking_account",
    title: "Add banking accounts",
    description:
      "Get a full read on your cash: where it goes, where it's wasted, what's free to put to work.",
    actionLabel: "Add an account",
    cardDescription: "Get a full read on your cash.",
    runningLabel: "Connected",
    runningName: "Bank accounts",
  },
  {
    stepId: 5,
    stepKey: "tracker",
    title: "Build a tracker",
    description:
      "Pick one thing for Ed to watch — a stock, your cash, an earnings date.",
    actionLabel: "Build a tracker",
    cardDescription: "Pick one thing for Ed to watch.",
    runningLabel: "Watching",
    runningName: "Tracker",
  },
  {
    stepId: 6,
    stepKey: "goal",
    title: "Activate your goal",
    description:
      "Once it's active, Ed runs the plan with you, keeps it moving, and tells you when something needs your call.",
    actionLabel: "Activate your goal",
    cardDescription: "Ed runs the plan with you.",
    runningLabel: "Running",
    runningName: "Activated Goal",
  },
];

/**
 * 根据 stepId 查找步骤元数据
 */
export function getStepMeta(stepId: number): ChecklistStepMeta | undefined {
  return CHECKLIST_STEP_META.find((m) => m.stepId === stepId);
}

/**
 * localStorage Key 工厂 — 信号位滑走防扰
 * 绑定 userId + 日期，次日自动恢复 — fe-spec §1.3 第 2 条
 */
export const getCalendarDismissKey = (userId: string): string =>
  `setup_checklist_dismissed_${userId}`;

/**
 * localStorage Key — 庆祝弹窗本地防重
 * 与服务端 POST /agents/preference 双重保障 — fe-spec §1.3 第 3 条
 */
export const getCelebrationDismissKey = (userId: string): string =>
  `setup_celebration_dismissed_${userId}`;

/**
 * localStorage Key — 庆祝弹窗关闭状态尚未同步到服务端（POST 失败）
 * 下次进入时补发，否则换设备会再次弹出 — PRD S1.13
 */
export const getCelebrationDismissPendingKey = (userId: string): string =>
  `setup_celebration_dismiss_pending_${userId}`;
